/**
 * Utility functions for message handling
 */

import type { QueryClient } from '@tanstack/react-query';
import { ExtendedChatMessage } from './chat-types';
import { parseStructuredContent, ContentSegment, ParsedTool } from './message-parser';
import { ChatMessage } from '@/hooks/use-command-messages';
import { normalizeAiosMultitaskToolInput } from '@/utils/command-tools';
import {
    attachSubagentRunsFromFetchedItems,
    readSubagentTaggedFields,
    type FetchedSubagentItem,
} from '@/utils/command-subagent-nesting';
import { SSEEvent, StreamStateManager } from '@/utils/sse-parser';

/**
 * Flip any cached `status: 'GENERATING'` message to `'COMPLETED'` without refetching.
 * Keeps `generatingAssistantId` from re-triggering the stream-reconnect effect after a
 * stream ends, now that we no longer invalidate the messages query on completion.
 */
export function markCachedMessagesCompleted(
    queryClient: QueryClient,
    agentId: string | null | undefined,
    sessionId: string | null | undefined,
) {
    if (!agentId || !sessionId) return;
    queryClient.setQueryData(
        ['command-messages', agentId, sessionId],
        (old: any) => {
            if (!old?.pages) return old;
            let changed = false;
            const pages = old.pages.map((page: any) => {
                if (!Array.isArray(page?.items)) return page;
                const items = page.items.map((item: any) => {
                    if (item?.status === 'GENERATING') {
                        changed = true;
                        return { ...item, status: 'COMPLETED' };
                    }
                    return item;
                });
                return changed ? { ...page, items } : page;
            });
            return changed ? { ...old, pages } : old;
        },
    );
}

/** Immediately clear in-flight assistant rows so UI does not flash "Thinking…" after stop. */
export function markLocalMessagesStopped(
    messages: ExtendedChatMessage[],
): ExtendedChatMessage[] {
    const stopOne = (message: ExtendedChatMessage): ExtendedChatMessage => {
        if (!message.isStreaming && message.status !== 'GENERATING') return message;
        return { ...message, isStreaming: false, status: 'COMPLETED' };
    };
    return messages.map((message) => {
        if (message.branches?.length) {
            return {
                ...stopOne(message),
                branches: message.branches.map(stopOne),
            };
        }
        return stopOne(message);
    });
}

/**
 * Unwrap tool result output from backend format:
 * content='{"data":...}' name='TOOL_NAME' tool_call_id='...'
 * Returns just the inner content (e.g. the JSON string).
 * Used for both streaming and loaded messages.
 */
export function unwrapToolResultOutput(raw: string): string {
    if (typeof raw !== 'string' || !raw.trim()) return raw;
    const contentPrefix = "content='";
    const idx = raw.indexOf(contentPrefix);
    if (idx === -1) return raw;
    const start = idx + contentPrefix.length;
    const afterContent = raw.slice(start);
    const endName = afterContent.indexOf("' name=");
    const endToolCallId = afterContent.indexOf("' tool_call_id=");
    let end = -1;
    if (endName !== -1 && endToolCallId !== -1) end = Math.min(endName, endToolCallId);
    else if (endName !== -1) end = endName;
    else if (endToolCallId !== -1) end = endToolCallId;
    if (end === -1) return raw;
    return afterContent.slice(0, end).trim();
}

/**
 * Serialize tool result payload to a string for display.
 * Used for both SSE tool_result and DB tool_call_result.
 * If payload has a .content field, use it (string or stringified object); else stringify the whole payload.
 */
export function serializeToolResultContent(payload: unknown): string {
    let str: string;
    if (payload != null && typeof payload === 'object' && 'content' in payload && (payload as { content?: unknown }).content != null) {
        const c = (payload as { content: unknown }).content;
        str = typeof c === 'string' ? c : JSON.stringify(c, null, 2);
    } else if (payload != null && typeof payload === 'object') {
        str = JSON.stringify(payload, null, 2);
    } else {
        str = String(payload ?? '');
    }
    return unwrapToolResultOutput(str);
}

/**
 * Clean JSON input by removing code block markers and parsing nested JSON strings
 */
function cleanJsonInput(input: any): any {
    if (!input || typeof input !== 'object') {
        return input;
    }

    // Handle arrays
    if (Array.isArray(input)) {
        return input.map(item => cleanJsonInput(item));
    }

    // Handle objects - clean each field
    const cleaned: any = {};

    for (const [key, value] of Object.entries(input)) {
        if (typeof value === 'string') {
            const trimmed = value.trim();

            // Check if this string has backticks or looks like JSON
            const hasBackticks = trimmed.includes('```');
            const looksLikeJson = (trimmed.startsWith('{') || trimmed.startsWith('[')) &&
                (trimmed.endsWith('}') || trimmed.endsWith(']') || hasBackticks);

            if (hasBackticks || looksLikeJson) {
                try {
                    // Remove code block markers
                    const cleanedStr = trimmed
                        .replace(/\n*```+\s*$/g, '') // Remove trailing ```
                        .replace(/^```+[a-z]*\n*/g, '') // Remove leading ```
                        .trim();

                    // Try to parse as JSON
                    let parsed: any = cleanedStr;
                    let attempts = 0;

                    while (typeof parsed === 'string' && attempts < 3) {
                        try {
                            parsed = JSON.parse(parsed);
                            attempts++;
                        } catch {
                            break;
                        }
                    }

                    // Use parsed value if successful
                    cleaned[key] = parsed;
                } catch {
                    cleaned[key] = value;
                }
            } else {
                cleaned[key] = value;
            }
        } else if (typeof value === 'object' && value !== null) {
            // Recursively clean nested objects
            cleaned[key] = cleanJsonInput(value);
        } else {
            cleaned[key] = value;
        }
    }

    return cleaned;
}

/**
 * Update an assistant message in-place whether it is top-level or nested in a branch group.
 * Mirrors the id-lookup path used by the SSE handler so streaming updates and merge effects
 * stay aligned.
 */
export function mapMessagesAssistantById(
    list: ExtendedChatMessage[],
    assistantId: string,
    updater: (msg: ExtendedChatMessage) => ExtendedChatMessage,
): ExtendedChatMessage[] {
    return list.map((msg) => {
        if (msg.id === assistantId) {
            return updater(msg);
        }
        if (msg.branches) {
            const branchIndex = msg.branches.findIndex((b) => b.id === assistantId);
            if (branchIndex !== -1) {
                const branches = [...msg.branches];
                branches[branchIndex] = updater(branches[branchIndex]);
                return { ...msg, branches };
            }
        }
        return msg;
    });
}

/** Collect every id/dbId present in a grouped (branched) message list — used to diff against local-only msgs. */
export function collectGroupedMessageIds(grouped: ExtendedChatMessage[]): Set<string> {
    const ids = new Set<string>();
    for (const m of grouped) {
        if (m.id) ids.add(m.id);
        if (m.dbId) ids.add(m.dbId);
        if (m.branches) {
            for (const b of m.branches) {
                if (b.id) ids.add(b.id);
                if (b.dbId) ids.add(b.dbId);
            }
        }
    }
    return ids;
}

/**
 * Group messages by parent_message_id to create branches
 * Messages with the same parent_message_id are alternative versions (branches)
 *
 * Only **user** message ids are valid branch parents. If the API sends
 * `parent_message_id` for an assistant that does not match any user row
 * (string mismatch, or parent points to a non-user message), the old logic
 * dropped that assistant entirely — a common case after regen. Such rows are
 * emitted as standalone so they are never lost.
 */
export function groupMessagesWithBranches(messages: ExtendedChatMessage[]): ExtendedChatMessage[] {
    const result: ExtendedChatMessage[] = [];
    const processedIds = new Set<string>();

    const userIds = new Set(
        messages.filter((m) => m.role === 'user').map((m) => String(m.id)),
    );

    // Only group assistants whose parent is a user message in this thread
    const branchGroups = new Map<string, ExtendedChatMessage[]>();
    for (const msg of messages) {
        if (msg.role !== 'assistant') continue;
        const parentId = (msg as any).parent_message_id;
        if (!parentId) continue;
        const parentKey = String(parentId);
        if (!userIds.has(parentKey)) continue;
        if (!branchGroups.has(parentKey)) {
            branchGroups.set(parentKey, []);
        }
        branchGroups.get(parentKey)!.push(msg);
    }
    const branchMemberIds = new Set(
        Array.from(branchGroups.values()).flat().map((msg) => msg.id),
    );

    for (const msg of messages) {
        if (processedIds.has(msg.id)) continue;
        if (msg.role === 'assistant' && branchMemberIds.has(msg.id)) continue;

        if (msg.role === 'user') {
            result.push(msg);
            processedIds.add(msg.id);

            const key = String(msg.id);
            const branches = branchGroups.get(key);
            if (branches && branches.length > 0) {
                // Keep versions in the order they were created so "1 of N" never renumbers when the
                // server marks a newer version ACTIVE; the ACTIVE one is selected through activeBranchIndex.
                const sortedBranches = [...branches].sort((a, b) => {
                    const aTime = new Date((a as any).created_at || a.timestamp).getTime();
                    const bTime = new Date((b as any).created_at || b.timestamp).getTime();
                    return aTime - bTime;
                });

                sortedBranches.forEach((b) => processedIds.add(b.id));

                if (sortedBranches.length === 1) {
                    result.push(sortedBranches[0]);
                } else {
                    const activeIndex = sortedBranches.findIndex((b) => (b as any).status === 'ACTIVE');
                    const mainMessage = sortedBranches[activeIndex >= 0 ? activeIndex : sortedBranches.length - 1];
                    result.push({
                        ...mainMessage,
                        branches: sortedBranches,
                        activeBranchIndex: activeIndex >= 0 ? activeIndex : sortedBranches.length - 1,
                    });
                }
            }
        } else if (msg.role === 'assistant') {
            // Branch members are marked when we process their user row; if we still
            // see the assistant, it was not in a user branch (id mismatch) or is a
            // top-level/legacy row — must not drop
            result.push(msg);
            processedIds.add(msg.id);
        }
    }

    return result;
}

/**
 * Parse fetched messages with structured content from API
 */
export function parseFetchedMessages(fetchedMessages: ChatMessage[]): ExtendedChatMessage[] {
    return fetchedMessages.map((msg) => {
        // Assistant messages still generating server-side should render as streaming
        const isGenerating = msg.role === 'assistant' && (msg as any).status === 'GENERATING';
        // Check if message has structured content from new API format
        if ((msg as any).structuredContent) {
            const structuredContent = (msg as any).structuredContent;

            // For user messages, extract text from any item with a text field
            if (msg.role === 'user') {
                const textContent = structuredContent
                    .map((item: any) => {
                        if (typeof item?.text === 'string') return item.text;
                        if (typeof item?.content === 'string') return item.content;
                        if (typeof item?.value === 'string') return item.value;
                        return '';
                    })
                    .filter(Boolean)
                    .join('\n');

                return {
                    ...msg,
                    content: textContent || msg.content || '',
                    isStreaming: false,
                };
            }

            // For assistant messages, parse structured content
            const contentSegments: ContentSegment[] = [];
            const nestedSubagentItems: FetchedSubagentItem[] = [];
            let reasoning = '';
            const tools: ParsedTool[] = [];
            const getItemType = (item: any): string => String(item?.type ?? '').toLowerCase();
            const getItemText = (item: any): string => {
                if (typeof item?.text === 'string') return item.text;
                if (typeof item?.content === 'string') return item.content;
                if (typeof item?.value === 'string') return item.value;
                if (typeof item?.message === 'string') return item.message;
                return '';
            };

            // First pass: collect all items and link observations to tools
            const items = structuredContent as any[];

            // First pass: collect tool outputs from observation OR tool_call_result (match by tool_call_id)
            const toolOutputMap = new Map<number, string>();
            const toolErrorIndexes = new Set<number>();
            for (let i = 0; i < items.length; i++) {
                const item = items[i];
                const itemType = getItemType(item);

                if ((itemType === 'observation' || itemType === 'tool_result') && item?.content != null) {
                    const unwrapped = unwrapToolResultOutput(String(item.content));
                    for (let j = i - 1; j >= 0; j--) {
                        const prevType = getItemType(items[j]);
                        if (prevType === 'tool_use' || prevType === 'tool_call' || prevType === 'tool-call') {
                            toolOutputMap.set(j, unwrapped);
                            break;
                        }
                    }
                } else if (itemType === 'tool_call_result' || itemType === 'tool_result') {
                    const toolCallId = item.tool_call_id ?? item.id;
                    const outputStr = serializeToolResultContent(item.content);
                    const targetIndex = items.findIndex((it: any, idx: number) => {
                        const type = getItemType(it);
                        if (!(type === 'tool_use' || type === 'tool_call' || type === 'tool-call')) return false;
                        return idx < i && (it.id === toolCallId || it.tool_call_id === toolCallId);
                    });
                    if (targetIndex !== -1) {
                        toolOutputMap.set(targetIndex, outputStr);
                        if (item.is_error) toolErrorIndexes.add(targetIndex);
                    }
                }
            }

            items.forEach((item: any, index: number) => {
                const itemType = getItemType(item);
                const { subagent, subagentCallId } = readSubagentTaggedFields(item as Record<string, unknown>);
                const isNested = Boolean(subagentCallId);

                if (
                    (itemType === 'text' ||
                        itemType === 'output_text' ||
                        itemType === 'assistant_text' ||
                        itemType === 'message') &&
                    getItemText(item)
                ) {
                    const cleanText = getItemText(item)
                        .replace(/```+$/m, '')
                        .replace(/^```+/m, '')
                        .trim();

                    if (cleanText) {
                        const segment: ContentSegment = { type: 'text', content: cleanText };
                        if (isNested) {
                            nestedSubagentItems.push({ subagentCallId, subagent, segment });
                        } else {
                            contentSegments.push(segment);
                        }
                    }
                } else if (itemType === 'tool_use' || itemType === 'tool_call' || itemType === 'tool-call') {
                    const toolName = item.name || item.tool_name || 'unknown';

                    // Choice prompts → selection tiles (not a generic tool card)
                    if (/ask_user_choice/i.test(String(toolName))) {
                        const cleanedInput = normalizeAiosMultitaskToolInput(
                            toolName,
                            cleanJsonInput(item.input) as Record<string, unknown>,
                        ) as Record<string, unknown>;
                        const rawOptions = Array.isArray(cleanedInput.options)
                            ? cleanedInput.options
                            : Array.isArray(item.options)
                              ? item.options
                              : [];
                        const options = rawOptions
                            .map((opt: Record<string, unknown>) => {
                                const value = String(opt?.value ?? opt?.id ?? '').trim();
                                const label = String(opt?.label ?? opt?.name ?? value).trim();
                                if (!label) return null;
                                return {
                                    value: value || label,
                                    label,
                                    subtitle:
                                        typeof opt?.subtitle === 'string'
                                            ? opt.subtitle
                                            : typeof opt?.description === 'string'
                                              ? opt.description
                                              : undefined,
                                    recommended: Boolean(opt?.recommended),
                                };
                            })
                            .filter(Boolean) as Array<{
                            value: string;
                            label: string;
                            subtitle?: string;
                            recommended?: boolean;
                        }>;

                        if (options.length > 0) {
                            contentSegments.push({
                                type: 'selection',
                                selection: {
                                    toolCallId: item.id || item.tool_call_id || `tool-${msg.id}-${index}`,
                                    name: String(toolName),
                                    prompt:
                                        typeof cleanedInput.prompt === 'string'
                                            ? cleanedInput.prompt
                                            : typeof item.prompt === 'string'
                                              ? item.prompt
                                              : undefined,
                                    options,
                                    multiSelect: Boolean(
                                        cleanedInput.multi_select ?? item.multi_select,
                                    ),
                                },
                            });
                            return;
                        }
                    }

                    const cleanedInput = normalizeAiosMultitaskToolInput(
                        toolName,
                        cleanJsonInput(item.input) as Record<string, unknown>,
                    );
                    const output = toolOutputMap.get(index);
                    const failed = toolErrorIndexes.has(index);

                    const tool: ParsedTool = {
                        toolCallId: item.id || item.tool_call_id || `tool-${msg.id}-${index}`,
                        type: `tool-${toolName.toLowerCase().replace(/\s+/g, '_')}`,
                        state: failed ? 'output-error' : output ? 'output-available' : 'input-available',
                        input: cleanedInput,
                        output: failed ? undefined : output,
                        errorText: failed ? output : undefined,
                        position: tools.length,
                        subagent,
                        subagentCallId,
                    };
                    tools.push(tool);
                    const segment: ContentSegment = { type: 'tool', tool };
                    if (isNested) {
                        nestedSubagentItems.push({ subagentCallId, subagent, segment });
                    } else {
                        contentSegments.push(segment);
                    }
                } else if (itemType === 'thinking' || itemType === 'reasoning') {
                    const thinkingText =
                        (typeof item.thinking === 'string' && item.thinking) ||
                        (typeof item.reasoning === 'string' && item.reasoning) ||
                        getItemText(item);
                    if (thinkingText) {
                        reasoning += (reasoning ? '\n\n' : '') + thinkingText;
                    }
                } else if (itemType === 'error') {
                    const errorText = getItemText(item);
                    if (errorText) {
                        contentSegments.push({ type: 'error', content: errorText });
                    }
                } else if (itemType === 'selection_request') {
                    const rawOptions = Array.isArray(item.options) ? item.options : [];
                    const options = rawOptions
                        .map((opt: Record<string, unknown>) => {
                            const value = String(opt?.value ?? opt?.id ?? '').trim();
                            const label = String(opt?.label ?? opt?.name ?? value).trim();
                            if (!label) return null;
                            const subtitle =
                                typeof opt?.subtitle === 'string'
                                    ? opt.subtitle
                                    : typeof opt?.description === 'string'
                                      ? opt.description
                                      : undefined;
                            return {
                                value: value || label,
                                label,
                                subtitle,
                                recommended: Boolean(opt?.recommended),
                            };
                        })
                        .filter(Boolean) as Array<{
                        value: string;
                        label: string;
                        subtitle?: string;
                        recommended?: boolean;
                    }>;

                    if (options.length > 0) {
                        contentSegments.push({
                            type: 'selection',
                            selection: {
                                toolCallId:
                                    typeof item.tool_call_id === 'string' ? item.tool_call_id : undefined,
                                name: typeof item.name === 'string' ? item.name : undefined,
                                prompt: typeof item.prompt === 'string' ? item.prompt : undefined,
                                options,
                                multiSelect: Boolean(item.multi_select),
                            },
                        });
                    }
                }
                // Skip 'observation' and 'tool_call_result' - handled in first pass and linked to tool_use by id/tool_call_id
            });

            const nestedContentSegments = attachSubagentRunsFromFetchedItems(
                contentSegments,
                nestedSubagentItems,
            );

            const fallbackContent = nestedContentSegments
                .filter((s) => s.type === 'text' && s.content)
                .map((s) => s.content)
                .join('\n\n');
            const streamError = nestedContentSegments.find((s) => s.type === 'error')?.content;

            return {
                ...msg,
                content: fallbackContent || msg.content || '',
                reasoning: reasoning || msg.reasoning,
                tools: tools.length > 0 ? tools : undefined,
                contentSegments: nestedContentSegments.length > 0 ? nestedContentSegments : undefined,
                isStreaming: isGenerating,
                ...(streamError ? { streamError } : {}),
            };
        } else if (
            msg.role === 'assistant' &&
            msg.content &&
            (msg.content.includes('Thought:') || msg.content.includes('Action:') || msg.content.includes('Final Answer:'))
        ) {
            // Old format - parse text-based content
            const parsed = parseStructuredContent(msg.content, false, msg.id);
            return {
                ...msg,
                content: parsed.content,
                reasoning: parsed.reasoning || msg.reasoning,
                tools: parsed.tools.length > 0 ? parsed.tools : undefined,
                contentSegments: parsed.contentSegments.length > 0 ? parsed.contentSegments : undefined,
                isStreaming: isGenerating,
            };
        }

        return {
            ...msg,
            isStreaming: isGenerating,
        };
    });
}

/**
 * Update streaming message with SSE event - TRUE Claude-style streaming!
 * No parsing needed, events are already structured!
 */
export function updateStreamingMessageSSE(
    msg: ExtendedChatMessage,
    systemMessageId: string,
    event: SSEEvent,
    streamManager: StreamStateManager
): ExtendedChatMessage {
    if (msg.id !== systemMessageId) {
        return msg;
    }

    // Process event through stream manager
    streamManager.processEvent(event);

    // Build content from stream manager
    const textContent = streamManager.getTextContent();
    const reasoning = streamManager.getReasoningContent();
    const toolCalls = streamManager.getToolCalls();
    const allBlocks = streamManager.getAllBlocks();

    // Build content segments for rendering - ONE segment per block
    const contentSegments: ContentSegment[] = [];
    for (const block of allBlocks) {
        if (block.type === 'text') {
            contentSegments.push({
                type: 'text',
                content: block.content,
            });
        } else if (block.type === 'tool_use') {
            const tool: ParsedTool = {
                toolCallId: block.toolId || `tool-${block.index}`,
                type: `tool-${(block.toolName || 'unknown').toLowerCase().replace(/\s+/g, '_')}`,
                state: 'output-available',
                input: block.toolInput,
                output: undefined,
                position: block.index,
            };
            contentSegments.push({
                type: 'tool',
                tool,
            });
        }
        // Skip thinking blocks - don't render in main content
    }

    // Convert tool calls to ParsedTool format
    const tools: ParsedTool[] = toolCalls.map((tc, idx) => ({
        toolCallId: tc.id,
        type: `tool-${tc.name.toLowerCase().replace(/\s+/g, '_')}`,
        state: 'output-available',
        input: tc.input,
        output: undefined,
        position: idx,
    }));

    return {
        ...msg,
        content: '', // Don't set content - use contentSegments instead to avoid duplication
        reasoning,
        tools,
        contentSegments,
        isStreaming: !streamManager.isMessageComplete(),
    };
}

/**
 * OLD: Update streaming message with plain text chunk
 * Keep for backward compatibility with plain text streaming
 */
export function updateStreamingMessage(
    msg: ExtendedChatMessage,
    systemMessageId: string,
    chunk: string
): ExtendedChatMessage {
    if (msg.id !== systemMessageId) {
        return msg;
    }

    // Accumulate raw content
    const rawContent = (msg.rawContent || msg.content || '') + chunk;

    // Parse immediately as chunks arrive (Claude-style incremental parsing)
    const parsed = parseStructuredContent(rawContent, true, msg.id);

    // Always return new object to force React re-render for smooth streaming
    return {
        ...msg,
        content: parsed.content || rawContent,
        rawContent: rawContent,
        reasoning: parsed.reasoning,
        tools: parsed.tools,
        contentSegments: parsed.contentSegments,
        isStreaming: true,
    };
}

/**
 * Finalize streaming message (parse complete content)
 */
export function finalizeStreamingMessage(
    msg: ExtendedChatMessage,
    systemMessageId: string
): ExtendedChatMessage {
    if (msg.id !== systemMessageId) {
        return msg;
    }

    const rawContent = msg.rawContent || msg.content || '';
    if (!rawContent) {
        return {
            ...msg,
            isStreaming: false,
        };
    }

    const parsed = parseStructuredContent(rawContent, false, msg.id);
    const finalContent = parsed.content && parsed.content.trim() ? parsed.content : rawContent;

    return {
        ...msg,
        content: finalContent,
        reasoning: parsed.reasoning,
        tools: parsed.tools,
        contentSegments: parsed.contentSegments.length > 0 ? parsed.contentSegments : (msg.contentSegments || []),
        isStreaming: false,
    };
}

/**
 * Stable key for matching the same logical user send across server id vs `temp-user-*` optimistic id.
 */
export function userMessageDedupeKey(msg: {
    role: string;
    content?: string;
    attachedFiles?: Array<{ id: string }>;
}): string | null {
    if (msg.role !== 'user') return null;
    const text = typeof msg.content === 'string' ? msg.content.trim() : '';
    const fileIds = [...(msg.attachedFiles ?? []).map((f) => String(f.id))].sort().join('\u0001');
    return `${text}\u0000${fileIds}`;
}

function userMessageSortTimeMs(m: ExtendedChatMessage): number {
    const raw = (m as { created_at?: string | Date }).created_at ?? m.timestamp;
    if (raw == null) return 0;
    const t = raw instanceof Date ? raw : new Date(raw);
    const ms = t.getTime();
    return Number.isFinite(ms) ? ms : 0;
}

/** Sort user rows for dedupe: time, then temp before non-temp on same ms (pair optimistic before persisted). */
function userMessagesDedupeSortOrder(a: ExtendedChatMessage, b: ExtendedChatMessage): number {
    const diff = userMessageSortTimeMs(a) - userMessageSortTimeMs(b);
    if (diff !== 0) return diff;
    const aTemp = a.id.startsWith('temp-user-');
    const bTemp = b.id.startsWith('temp-user-');
    if (aTemp !== bTemp) return aTemp ? -1 : 1;
    return a.id.localeCompare(b.id);
}

/**
 * After a refetch/merge, the same logical user send can appear twice (persisted row + optimistic `temp-user-*`).
 * Walk user messages in chronological order: each server row consumes the oldest unmatched `temp-user-*`
 * with the same `userMessageDedupeKey`. Never pairs a new temp with an older server row (same text twice),
 * which index-based "closest server before" did when the newest persisted row was missing from the response.
 */
export function dedupeTempUserDuplicatesWithServer(list: ExtendedChatMessage[]): ExtendedChatMessage[] {
    const realIds = new Set(list.filter(m => !m.id.startsWith('temp-')).map(m => m.id));

    const userMsgs = list.filter((m) => m.role === 'user' && userMessageDedupeKey(m));
    const sorted = [...userMsgs].sort(userMessagesDedupeSortOrder);

    const removeUserTemp = new Set<string>();
    const pendingByKey = new Map<string, string[]>();

    for (const m of sorted) {
        const k = userMessageDedupeKey(m);
        if (!k) continue;
        if (m.id.startsWith('temp-user-')) {
            const q = pendingByKey.get(k) ?? [];
            q.push(m.id);
            pendingByKey.set(k, q);
        } else {
            const q = pendingByKey.get(k);
            if (q && q.length > 0) {
                removeUserTemp.add(q.shift()!);
                pendingByKey.set(k, q);
            }
        }
    }

    return list.filter((m) => {
        if (removeUserTemp.has(m.id)) return false;
        if (!m.id.startsWith('temp-')) return true;
        if (m.dbId && realIds.has(m.dbId)) return false;
        return true;
    });
}

/**
 * Merge fetched messages with local messages, removing duplicates
 * Preserves temporary messages that have a dbId (saved to database) or are still streaming
 */
export function mergeMessages(
    fetchedMessages: ChatMessage[],
    localMessages: ExtendedChatMessage[]
): ExtendedChatMessage[] {
    const fetchedMessageIds = new Set(fetchedMessages.map((m) => m.id));
    const fetchedDbIds = new Set(fetchedMessages.map((m) => m.id).filter(Boolean));

    // Keep temporary messages that:
    // 1. Have a dbId (saved to database) - they'll be replaced when fetched
    // 2. Are still streaming (isStreaming: true)
    // 3. Are part of branches that might not be in fetched messages yet
    const newLocalMessages = localMessages.filter((m) => {
        // Keep temporary messages that have a dbId (they're saved, just not fetched yet)
        if (m.id.startsWith('temp-') && m.dbId) {
            // Only keep if not in fetched messages by dbId
            return !fetchedDbIds.has(m.dbId);
        }
        
        // Keep temporary messages that are still streaming
        if (m.id.startsWith('temp-') && m.isStreaming) {
            return true;
        }
        
        // Keep temporary messages that are part of branches
        if (m.id.startsWith('temp-') && m.branches) {
            return true;
        }

        // Keep temp messages not yet persisted — cleaned up once dbId is confirmed.
        // Exception: completed temps that carry a streamError are dropped so the DB row
        // (which parseFetchedMessages also decorates with streamError) replaces them cleanly.
        if (m.id.startsWith('temp-') && !m.dbId) {
            if (!m.isStreaming && (m as any).streamError) return false;
            return true;
        }

        // Remove other temporary messages
        if (m.id.startsWith('temp-')) {
            return false;
        }
        
        // Remove if already in fetched messages
        if (fetchedMessageIds.has(m.id)) {
            return false;
        }

        return true;
    });

    // Parse fetched messages and group by branches
    const parsedFetchedMessages = parseFetchedMessages(fetchedMessages);
    const groupedMessages = groupMessagesWithBranches(parsedFetchedMessages);

    /** Local has richer content than fetched when it finished streaming but the cache wasn't refetched. */
    const hasStreamedContent = (m: ExtendedChatMessage | undefined): m is ExtendedChatMessage =>
        !!m && !m.isStreaming && ((m.contentSegments?.length ?? 0) > 0 || (typeof m.content === 'string' && m.content.length > 0));

    /** Local copy of an assistant row, top-level or inside a regeneration group. */
    const findLocalAssistant = (id: string): ExtendedChatMessage | undefined => {
        for (const m of localMessages) {
            if (m.role !== 'assistant') continue;
            if (m.id === id || m.dbId === id) return m;
            const branch = m.branches?.find((b) => b.id === id || b.dbId === id);
            if (branch) return branch;
        }
        return undefined;
    };

    /** One branch of a regeneration group: if the server still says GENERATING after ours finished, keep ours. */
    const reconcileBranch = (serverBranch: ExtendedChatMessage): ExtendedChatMessage => {
        const local = findLocalAssistant(serverBranch.id);
        const serverGenerating =
            serverBranch.isStreaming || (serverBranch as { status?: string }).status === 'GENERATING';
        if (local && local.isStreaming === false && serverGenerating) {
            const base = hasStreamedContent(local)
                ? {
                      ...local,
                      branches: undefined,
                      activeBranchIndex: undefined,
                      parent_message_id:
                          (serverBranch as { parent_message_id?: string | null }).parent_message_id ??
                          (local as { parent_message_id?: string | null }).parent_message_id,
                  }
                : serverBranch;
            return {
                ...base,
                isStreaming: false,
                status: 'COMPLETED',
                ...(local.suggestions?.length && !base.suggestions?.length ? { suggestions: local.suggestions } : {}),
            };
        }
        return local?.contextUsage && !serverBranch.contextUsage
            ? { ...serverBranch, contextUsage: local.contextUsage }
            : serverBranch;
    };

    // Merge: prefer fetched, but keep local when it has streamed content the cache hasn't caught up to.
    const merged: ExtendedChatMessage[] = [];
    const usedDbIds = new Set<string>();

    // Add fetched messages (swap in local version when local has content the fetched row is missing).
    for (const msg of groupedMessages) {
        if (msg.branches && msg.branches.length > 1) {
            // A regeneration group: the local group can sit under another id, so match branch by branch.
            const branches = msg.branches.map(reconcileBranch);
            const activeIndex = msg.activeBranchIndex ?? branches.length - 1;
            const main = branches[activeIndex] ?? branches[branches.length - 1];
            merged.push({ ...main, branches, activeBranchIndex: msg.activeBranchIndex });
            if (msg.id) usedDbIds.add(msg.id);
            continue;
        }
        const localMatch = localMessages.find(
            (m) => m.role === 'assistant' && (m.id === msg.id || m.dbId === msg.id),
        );
        const preferLocal = hasStreamedContent(localMatch) && !hasStreamedContent(msg);
        if (preferLocal) {
            // Overlay `parent_message_id` from the server row — the local copy was created
            // without one, and the parent-anchor sort below needs it to pin the assistant
            // next to its user message. Without this, the local `new Date()` timestamp
            // (which predates the server's `created_at`) sorts the bubble above the user.
            merged.push({
                ...localMatch!,
                parent_message_id:
                    (msg as { parent_message_id?: string | null }).parent_message_id ??
                    (localMatch as { parent_message_id?: string | null })?.parent_message_id,
            });
        } else if (
            localMatch &&
            localMatch.isStreaming === false &&
            (msg.isStreaming || (msg as { status?: string }).status === 'GENERATING')
        ) {
            // Local says complete, server cache is lagging — trust local to avoid a stuck streaming orb.
            merged.push({
                ...msg,
                isStreaming: false,
                status: 'COMPLETED',
                ...(localMatch.contextUsage && !msg.contextUsage
                    ? { contextUsage: localMatch.contextUsage }
                    : {}),
            });
        } else {
            merged.push(
                localMatch?.contextUsage && !msg.contextUsage
                    ? { ...msg, contextUsage: localMatch.contextUsage }
                    : msg
            );
        }
        if (msg.id) usedDbIds.add(msg.id);
    }
    
    // Add local messages that aren't duplicates
    for (const msg of newLocalMessages) {
        // Skip if dbId is already in fetched messages
        if (msg.dbId && usedDbIds.has(msg.dbId)) {
            continue;
        }
        merged.push(msg);
        if (msg.dbId) usedDbIds.add(msg.dbId);
    }

    // Chronological sort so messages that fell off the page-1 boundary (after pagination
    // slide) still appear in the right slot instead of being dumped at the bottom.
    const toMs = (m: ExtendedChatMessage): number => {
        const raw = (m as { created_at?: string | Date }).created_at ?? m.timestamp;
        if (raw == null) return 0;
        const t = raw instanceof Date ? raw : new Date(raw);
        const ms = t.getTime();
        return Number.isFinite(ms) ? ms : 0;
    };
    // Anchor a still-streaming temp assistant to the latest persisted user message:
    // server-assigned created_at can post-date the click moment used for the temp's local timestamp.
    const lastPersistedUserMs = merged.reduce((max, m) => {
        if (m.role !== 'user' || m.id.startsWith('temp-')) return max;
        const ms = toMs(m);
        return ms > max ? ms : max;
    }, 0);
    // Pair assistants to their user message by parent_message_id, not array order:
    // a regenerated branch's created_at can post-date later user turns, so sorting
    // purely by time would drop the grouped row below an unrelated user message.
    const userMsByDbId = new Map<string, number>();
    for (const m of merged) {
        if (m.role !== 'user') continue;
        const ms = toMs(m);
        userMsByDbId.set(String(m.id), ms);
        if (m.dbId) userMsByDbId.set(String(m.dbId), ms);
    }
    const sortMs = (m: ExtendedChatMessage): number => {
        if (m.role === 'assistant') {
            const parentId = (m as any).parent_message_id;
            if (parentId) {
                const parentMs = userMsByDbId.get(String(parentId));
                if (parentMs != null) return parentMs + 1;
            }
        }
        // A locally-streaming assistant has a client-side `new Date()` timestamp that
        // predates the server's `created_at` for the paired user row, so sorting by
        // raw time would float the bubble above the user. Anchor it just after the
        // last persisted user instead. (Don't gate on a `temp-` id prefix: the id
        // gets swapped to the real one on `message_start`, but the anchor still has
        // to apply.)
        if (
            m.role === 'assistant' &&
            m.isStreaming &&
            lastPersistedUserMs > 0
        ) {
            return Math.max(toMs(m), lastPersistedUserMs + 1);
        }
        return toMs(m);
    };
    merged.sort((a, b) => sortMs(a) - sortMs(b));

    return dedupeTempUserDuplicatesWithServer(merged);
}

